import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { TUploadedFile } from '@/shared/types';
import type { UseFileUploadProps, UseFileUploadReturn } from '../types';
import { captureException } from '@/modules/sentry';
import { serializeFileValue } from '../utils/fileUpload';
import { uploadFileToBishop } from '@/shared/utils/file-upload';

/**
 * Hook for handling file upload state in automation input forms.
 *
 * Wraps the shared `uploadFileToBishop` utility with form-specific
 * state management: file list, removal, serialization via `onChange`,
 * and per-file upload counting.
 *
 * @param props - Hook configuration
 * @returns File upload state and operations
 */
export const useAutomationInputFileUpload = ({
  organizationId,
  workspaceId,
  multiple,
  onChange,
}: UseFileUploadProps): UseFileUploadReturn => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<TUploadedFile[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);

  const { mutate: uploadFileMutation } = useMutation({
    mutationFn: async ({
      organization,
      workspace,
      file,
    }: {
      organization: string;
      workspace: string;
      file: File;
    }) => {
      return uploadFileToBishop(file, organization, workspace);
    },
    onSuccess: (uploadedFile: TUploadedFile) => {
      setUploadedFiles(prev => {
        const updated = multiple ? [...prev, uploadedFile] : [uploadedFile];
        onChange(serializeFileValue(updated, multiple));
        return updated;
      });
      setUploadError(null);
      setUploadingCount(prev => prev - 1);
    },
    onError: (err: Error) => {
      setUploadError(err.message || 'Failed to upload file');
      captureException(err);
      setUploadingCount(prev => prev - 1);
    },
  });

  /**
   * Processes and uploads files.
   *
   * @param files - Files to upload
   */
  const processFiles = (files: FileList | File[]) => {
    if (!files || files.length === 0) return;

    setUploadError(null);

    if (multiple) {
      setUploadingCount(files.length);
      Array.from(files).forEach(file => {
        uploadFileMutation({
          organization: organizationId,
          workspace: workspaceId,
          file,
        });
      });
    } else {
      const file = files[0];
      setUploadingCount(1);
      uploadFileMutation({
        organization: organizationId,
        workspace: workspaceId,
        file,
      });
    }
  };

  /**
   * Removes a file by its resource name.
   *
   * @param resourceName - Resource name of the file to remove
   */
  const removeFile = (resourceName: string) => {
    setUploadedFiles(prev => {
      const updated = prev.filter(f => f.resourceName !== resourceName);
      onChange(serializeFileValue(updated, multiple));
      return updated;
    });
  };

  return {
    uploadedFiles,
    setUploadedFiles,
    uploadError,
    isUploading: uploadingCount > 0,
    uploadingCount,
    processFiles,
    removeFile,
  };
};
